import React from 'react';
import { motion } from 'framer-motion';
import { FileText, ArrowLeft, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const TermsOfServicePage = () => {
  const lastUpdate = "19 de febrero de 2026";
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Link to="/" className="inline-flex items-center text-slate-400 hover:text-white mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver al inicio
          </Link>
          
          <div className="flex items-center space-x-4 mb-4">
            <div className="p-3 bg-emerald-500/20 rounded-xl">
              <FileText className="w-8 h-8 text-emerald-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">Términos y Condiciones de Uso</h1>
              <p className="text-slate-400">Última actualización: {lastUpdate}</p>
            </div>
          </div>
        </motion.div>

        {/* Important Notice */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-6"
        >
          <div className="flex items-start space-x-3">
            <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-amber-200 text-sm">
              <strong>Importante:</strong> Al utilizar NotaryOS, usted acepta estos términos y condiciones 
              en su totalidad. Si no está de acuerdo con alguna parte, no debe utilizar nuestros servicios.
            </p>
          </div>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-slate-800/50 rounded-2xl p-8 border border-slate-700 space-y-8"
        >
          {/* Definiciones */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">1. Definiciones</h2>
            <ul className="text-slate-300 space-y-2">
              <li><strong>"Plataforma":</strong> El sitio web y aplicación NotaryOS</li>
              <li><strong>"Usuario":</strong> Toda persona que accede o utiliza la Plataforma</li>
              <li><strong>"Servicios":</strong> Todas las funcionalidades ofrecidas por NotaryOS</li>
              <li><strong>"Contrato":</strong> Documento legal generado a través de la Plataforma</li>
              <li><strong>"Contenido":</strong> Información, textos, documentos subidos por usuarios</li>
            </ul>
          </section>

          {/* Objeto del Servicio */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">2. Objeto del Servicio</h2>
            <p className="text-slate-300 leading-relaxed">
              NotaryOS es una plataforma tecnológica que facilita la creación, gestión y firma de 
              documentos legales mediante herramientas de inteligencia artificial. <strong>NotaryOS 
              NO es un estudio jurídico</strong> ni provee asesoría legal personalizada. Los contratos 
              generados son plantillas que deben ser revisadas por un abogado antes de su uso en 
              situaciones legales complejas.
            </p>
          </section>

          {/* Registro y Cuenta */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">3. Registro y Cuenta de Usuario</h2>
            <p className="text-slate-300 mb-4">Para utilizar nuestros servicios, usted debe:</p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
              <li>Ser mayor de 18 años o contar con autorización de un representante legal</li>
              <li>Proporcionar información veraz, completa y actualizada</li>
              <li>Mantener la confidencialidad de sus credenciales de acceso</li>
              <li>Notificar inmediatamente cualquier uso no autorizado de su cuenta</li>
              <li>No crear múltiples cuentas ni ceder su cuenta a terceros</li>
            </ul>
          </section>

          {/* Uso Aceptable */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">4. Uso Aceptable de la Plataforma</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                <div className="flex items-center space-x-2 mb-3">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <h3 className="font-semibold text-green-400">Permitido</h3>
                </div>
                <ul className="text-slate-300 text-sm space-y-1">
                  <li>• Crear contratos para fines legítimos</li>
                  <li>• Usar plantillas para negocios propios</li>
                  <li>• Compartir contratos con partes involucradas</li>
                  <li>• Solicitar firmas a terceros</li>
                </ul>
              </div>
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                <div className="flex items-center space-x-2 mb-3">
                  <XCircle className="w-5 h-5 text-red-400" />
                  <h3 className="font-semibold text-red-400">Prohibido</h3>
                </div>
                <ul className="text-slate-300 text-sm space-y-1">
                  <li>• Crear documentos fraudulentos</li>
                  <li>• Suplantar identidad de terceros</li>
                  <li>• Usar para actividades ilegales</li>
                  <li>• Revender o redistribuir el servicio</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Propiedad Intelectual */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">5. Propiedad Intelectual</h2>
            <p className="text-slate-300 leading-relaxed">
              Todos los derechos de propiedad intelectual sobre la Plataforma, incluyendo software, 
              diseño, logotipos, plantillas base y algoritmos de IA, pertenecen a NotaryOS SpA. 
              Los usuarios conservan la propiedad sobre el contenido que crean, otorgando a NotaryOS 
              una licencia limitada para procesarlo y almacenarlo según sea necesario para proveer 
              el servicio.
            </p>
          </section>

          {/* Planes y Pagos */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">6. Planes y Pagos</h2>
            <p className="text-slate-300 mb-4">NotaryOS ofrece diferentes planes de suscripción:</p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
              <li><strong>Plan Gratuito:</strong> Funcionalidades básicas con límites de uso</li>
              <li><strong>Plan Premium:</strong> Acceso completo a todas las funcionalidades</li>
              <li><strong>Plan Empresarial:</strong> Soluciones personalizadas para organizaciones</li>
            </ul>
            <p className="text-slate-300 mt-4">
              Los pagos se procesan de forma segura a través de proveedores certificados (Stripe). 
              Las suscripciones se renuevan automáticamente salvo cancelación previa. No se realizan 
              reembolsos por períodos parciales de uso.
            </p>
          </section>

          {/* Limitación de Responsabilidad */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">7. Limitación de Responsabilidad</h2>
            <div className="bg-slate-700/50 rounded-xl p-4">
              <p className="text-slate-300 leading-relaxed">
                NotaryOS provee herramientas tecnológicas y <strong>no se responsabiliza por:</strong>
              </p>
              <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4 mt-3">
                <li>El contenido de los contratos creados por los usuarios</li>
                <li>Consecuencias legales derivadas del uso de los documentos</li>
                <li>Pérdidas económicas por decisiones basadas en nuestras herramientas</li>
                <li>Interrupciones temporales del servicio por mantenimiento</li>
                <li>Acciones de terceros que vulneren la seguridad</li>
              </ul>
              <p className="text-amber-400 mt-4 text-sm">
                <strong>Recomendación:</strong> Para asuntos legales complejos, consulte siempre con 
                un abogado colegiado.
              </p>
            </div>
          </section>

          {/* Ley Aplicable */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">8. Ley Aplicable y Jurisdicción</h2>
            <p className="text-slate-300 leading-relaxed">
              Estos términos se rigen por las leyes de la República de Chile. Cualquier controversia 
              será sometida a los tribunales ordinarios de justicia de la ciudad de Santiago, 
              renunciando las partes a cualquier otro fuero que pudiera corresponderles.
            </p>
          </section>

          {/* Modificaciones */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">9. Modificaciones</h2>
            <p className="text-slate-300 leading-relaxed">
              NotaryOS se reserva el derecho de modificar estos términos en cualquier momento. 
              Los cambios serán notificados por correo electrónico y/o mediante aviso en la plataforma 
              con al menos 30 días de anticipación. El uso continuado de la plataforma después de 
              dicho período constituye aceptación de los nuevos términos.
            </p>
          </section>

          {/* Terminación */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">10. Terminación</h2>
            <p className="text-slate-300 leading-relaxed">
              NotaryOS puede suspender o terminar su cuenta si: (a) viola estos términos; (b) utiliza 
              la plataforma para fines ilegales; (c) no paga las suscripciones correspondientes; o 
              (d) por requerimiento de autoridad competente. Usted puede cancelar su cuenta en 
              cualquier momento desde la configuración de su perfil.
            </p>
          </section>

          {/* Contacto */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">11. Contacto</h2>
            <p className="text-slate-300">
              Para consultas sobre estos términos, contáctenos en:
            </p>
            <div className="mt-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
              <p className="text-emerald-400">
                <strong>Email:</strong> legal@notaryos.cl<br />
                <strong>Soporte:</strong> soporte@notaryos.cl
              </p>
            </div>
          </section>
        </motion.div>

        {/* Footer */}
        <div className="mt-8 text-center text-slate-500 text-sm">
          © 2026 NotaryOS SpA. Todos los derechos reservados.
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
